import { memo, useCallback, useMemo, useState, type UIEvent } from 'react';
import { Bell, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import {
  NotificationAudience,
  type NotificationItem,
} from '@gokrew/models/notifications';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover';
import { formatNotificationGroupLabel } from '@/utility/notifications';
import type { CreatedNotificationHooks } from '@/hooks/createUseNotifications';
import {
  NotificationRow,
  type NotificationInlineAction,
} from './NotificationRow';
import styles from './Notifications.module.css';

enum NotificationTab {
  All = 'all',
  Unread = 'unread',
  Approvals = 'approvals',
}

const MAX_BADGE_COUNT = 99;

/** Distance from the bottom of the feed, in px, that pulls the next page in. */
const LOAD_MORE_OFFSET = 80;

const TABS = [
  NotificationTab.All,
  NotificationTab.Unread,
  NotificationTab.Approvals,
];

export interface CreateNotificationsOptions {
  /** The hook set from `createUseNotifications`, bound to this portal. */
  hooks: CreatedNotificationHooks;
  /**
   * Where a click on this item goes. `null` renders the item non-clickable —
   * which is what an unknown subject type, or one this portal has no screen
   * for, must resolve to. Use `resolveNotificationHref` with the portal's own
   * route map.
   */
  resolveHref: (item: NotificationItem) => string | null;
}

/**
 * Factory that creates the header notification bell + panel bound to one
 * portal's hooks and routes. The panel owns that app's single SSE stream, so
 * the component it returns must be rendered exactly once — the backend caps a
 * user at 3 concurrent streams per company.
 *
 * Usage:
 *   import { createNotifications } from '@gokrew/ui';
 *
 *   export default createNotifications({
 *     hooks: notificationHooks,
 *     resolveHref: (item) => resolveNotificationHref(item, SUBJECT_ROUTE),
 *   });
 */
export function createNotifications({
  hooks,
  resolveHref,
}: CreateNotificationsOptions) {
  const {
    useNotificationFeed,
    useNotificationMutations,
    useNotificationsUnreadCount,
    useNotificationStream,
    useRefreshNotifications,
  } = hooks;

  const Notifications = () => {
    const { i18n, t } = useTranslation(TranslationNS.Notification);
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState<NotificationTab>(NotificationTab.All);

    // Mounted here, and only here: the bell owns the app's single stream, and
    // both REST queries below wait for it so the stream is opened first.
    const { hasSettled } = useNotificationStream();

    const { data: unreadCount = 0 } = useNotificationsUnreadCount({
      enabled: hasSettled,
    });
    const refreshNotifications = useRefreshNotifications();
    const { markRead, markAllRead, remove, runAction } =
      useNotificationMutations();
    const { mutate: markItemRead } = markRead;
    const { mutate: deleteItem } = remove;
    const { mutateAsync: runActionAsync } = runAction;

    // The feed loads only once the panel is opened — the badge keeps the
    // trigger accurate without it.
    const {
      groups,
      isLoading,
      isError,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
    } = useNotificationFeed({
      unreadOnly: tab === NotificationTab.Unread,
      audience:
        tab === NotificationTab.Approvals
          ? NotificationAudience.APPROVER
          : undefined,
      enabled: open && hasSettled,
    });

    // Both tabs filter server-side, so a page holds only rows this tab shows —
    // nothing is dropped here. Each item's href is resolved once and carried on
    // the row, since the route builders read off `item.context` and the row
    // needs the same value twice: to know it is clickable, and to navigate on
    // click.
    const visibleGroups = useMemo(
      () =>
        groups.map((group) => ({
          date: group.date,
          rows: group.items.map((item) => ({ item, href: resolveHref(item) })),
        })),
      [groups]
    );

    const loadMore = useCallback(() => {
      if (!hasNextPage || isFetchingNextPage) return;
      fetchNextPage();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const handleFeedScroll = useCallback(
      (event: UIEvent<HTMLDivElement>) => {
        const el = event.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_OFFSET)
          loadMore();
      },
      [loadMore]
    );

    const handleOpenChange = useCallback(
      (next: boolean) => {
        setOpen(next);
        if (next) refreshNotifications();
      },
      [refreshNotifications]
    );

    const handleOpenItem = useCallback(
      (item: NotificationItem, href: string | null) => {
        if (!item.isRead) markItemRead(item.id);
        if (!href) return;

        setOpen(false);
        navigate(href);
      },
      [markItemRead, navigate]
    );

    const handleDelete = useCallback(
      (item: NotificationItem) => deleteItem(item.id),
      [deleteItem]
    );

    const handleAction = useCallback(
      (
        item: NotificationItem,
        action: NotificationInlineAction,
        promptValue?: string
      ) => {
        if (!item.isRead) markItemRead(item.id);
        return runActionAsync({
          request: action.request,
          category: item.category,
          actionId: action.id,
          promptValue,
        });
      },
      [markItemRead, runActionAsync]
    );

    // Opening the panel before the stream settles still reads as loading, not
    // as an empty feed.
    const isFeedPending = !hasSettled || isLoading;
    const showEmpty = !isFeedPending && !isError && !visibleGroups.length;

    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={styles.trigger}
            aria-label={t('panel.open')}
            title={t('panel.open')}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className={styles.badge}>
                {unreadCount > MAX_BADGE_COUNT
                  ? `${MAX_BADGE_COUNT}+`
                  : unreadCount}
              </span>
            )}
          </button>
        </PopoverTrigger>

        <PopoverContent align="end" sideOffset={12} className={styles.panel}>
          <header className={styles.panelHeader}>
            <div className={styles.panelHeading}>
              <h2 className={styles.panelTitle}>{t('panel.title')}</h2>
              {unreadCount > 0 && (
                <span className={styles.panelCount}>
                  {t('panel.unread_count', { count: unreadCount })}
                </span>
              )}
            </div>
            <button
              type="button"
              className={styles.markAllBtn}
              disabled={!unreadCount || markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            >
              {markAllRead.isPending ? (
                <LoadingSpinner size={14} className="text-current" />
              ) : (
                <Check size={14} />
              )}
              {t('panel.mark_all_read')}
            </button>
          </header>

          <div className={styles.tabs} role="tablist">
            {TABS.map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={tab === value}
                className={`${styles.tab} ${
                  tab === value ? styles.tabActive : ''
                }`}
                onClick={() => setTab(value)}
              >
                {value === NotificationTab.Unread && unreadCount > 0
                  ? t('tabs.unread_count', { count: unreadCount })
                  : t(`tabs.${value}`)}
              </button>
            ))}
          </div>

          <div className={styles.feed} onScroll={handleFeedScroll}>
            {isFeedPending && (
              <div className={styles.feedState}>
                <LoadingSpinner size={20} className="text-current" />
                <p className={styles.emptyMessage}>{t('panel.loading')}</p>
              </div>
            )}

            {!isFeedPending && isError && (
              <div className={styles.feedState}>
                <p className={styles.emptyMessage}>{t('panel.error')}</p>
              </div>
            )}

            {showEmpty && (
              <div className={styles.empty}>
                <Bell size={28} className={styles.emptyIcon} />
                <p className={styles.emptyTitle}>{t('panel.empty_title')}</p>
                <p className={styles.emptyMessage}>
                  {t(`panel.empty_${tab}`, {
                    defaultValue: t('panel.empty_all'),
                  })}
                </p>
              </div>
            )}

            {visibleGroups.map((group) => (
              <section key={group.date} className={styles.group}>
                <h3 className={styles.groupLabel}>
                  {formatNotificationGroupLabel(group.date, i18n.language, {
                    today: t('date.today'),
                    yesterday: t('date.yesterday'),
                  })}
                </h3>
                <ul className={styles.list}>
                  {group.rows.map(({ item, href }) => (
                    <NotificationRow
                      key={item.id}
                      item={item}
                      href={href}
                      onOpen={handleOpenItem}
                      onDelete={handleDelete}
                      onAction={handleAction}
                    />
                  ))}
                </ul>
              </section>
            ))}

            {isFetchingNextPage && (
              <div className={styles.feedFooter}>
                <LoadingSpinner size={16} className="text-current" />
                {t('panel.loading_more')}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  return memo(Notifications);
}
